import React, { useEffect, useRef } from "react";

interface ConfettiPiece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  size: number;
  opacity: number;
}

interface ConfettiProps {
  isActive: boolean;
  duration?: number;
}

const Confetti: React.FC<ConfettiProps> = ({ isActive, duration = 3000 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const piecesRef = useRef<ConfettiPiece[]>([]);
  const startTimeRef = useRef<number>(0);

  const colors = [
    "#FF6B6B", // Red
    "#4ECDC4", // Teal
    "#45B7D1", // Blue
    "#96CEB4", // Green
    "#FFEAA7", // Yellow
    "#DDA0DD", // Plum
    "#98D8C8", // Mint
    "#F7DC6F", // Gold
    "#BB8FCE", // Lavender
    "#85C1E9", // Sky Blue
  ];

  const createConfettiPiece = (x: number, y: number): ConfettiPiece => ({
    x,
    y,
    vx: (Math.random() - 0.5) * 8 - 2,
    vy: Math.random() * -10 - 5,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 20,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: Math.random() * 8 + 4,
    opacity: 1,
  });

  const initConfetti = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const centerX = rect.width / 2;
    const startY = rect.height * 0.1;

    piecesRef.current = Array.from({ length: 300 }, () => {
      // Spread confetti across a wider area for more natural distribution
      const startX = centerX + (Math.random() - 0.5) * 200;
      return createConfettiPiece(startX, startY);
    });
    startTimeRef.current = Date.now();
  };

  const animate = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const elapsed = Date.now() - startTimeRef.current;
    const progress = elapsed / duration;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Update and draw pieces
    piecesRef.current.forEach((piece, index) => {
      // Apply gravity
      piece.vy += 0.3;
      piece.x += piece.vx;
      piece.y += piece.vy;
      piece.rotation += piece.rotationSpeed * 0.01;

      // Fade out over time
      if (progress > 0.7) {
        piece.opacity = Math.max(0, 1 - (progress - 0.7) / 0.3);
      }

      // Draw piece
      ctx.save();
      ctx.translate(piece.x, piece.y);
      ctx.rotate((piece.rotation * Math.PI) / 180);
      ctx.globalAlpha = piece.opacity;

      // Draw confetti piece with variety
      ctx.fillStyle = piece.color;

      // Randomly choose between rectangle and circle shapes
      if (Math.random() > 0.7) {
        // Circle confetti
        ctx.beginPath();
        ctx.arc(0, 0, piece.size / 2, 0, 2 * Math.PI);
        ctx.fill();
      } else {
        // Rectangle confetti
        ctx.fillRect(
          -piece.size / 2,
          -piece.size / 2,
          piece.size,
          piece.size * 0.3
        );
      }

      // Add some sparkle
      if (Math.random() > 0.92) {
        ctx.fillStyle = "white";
        ctx.globalAlpha = piece.opacity * 0.8;
        ctx.fillRect(-1, -1, 2, 2);
      }

      ctx.restore();
    });

    // Continue animation if not finished
    if (elapsed < duration) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      // Clean up
      piecesRef.current = [];
    }
  };

  useEffect(() => {
    if (isActive) {
      initConfetti();
      animate();
    } else {
      // Clean up animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      piecesRef.current = [];
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, duration]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
      style={{ width: "100vw", height: "100vh" }}
    />
  );
};

export default Confetti;
